/**
 * AD Manager — Active Directory Management
 *
 * Module page pro správu uživatelů v AD přes MCP toolu.
 * Žádný proprietary HTTP API — volá výhradně `api.mcp('ad.*')`.
 * Multi-doménová podpora (selector v headeru, persistent v localStorage).
 *
 * Backend: modules/ad-manager/tools/ad.*.ts
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Modal,
  PageHeader,
  Select,
  StatCard,
  StatsGrid,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '../../components/ui';
import { api } from '../../lib/api';
import {
  Users,
  UserPlus,
  KeyRound,
  Search,
  UserCheck,
  Shield,
  ChevronRight,
  Group as GroupsIcon,
  Building2,
  ScrollText,
  Upload,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';

const ACTIVE_DOMAIN_KEY = 'ad-manager-active-domain';

// ── Types ───────────────────────────────────────────────────────────────────

interface AdUser {
  distinguishedName: string;
  sAMAccountName?: string;
  displayName: string;
  mail?: string;
  memberOf?: string | string[];
  description?: string;
  [key: string]: unknown;
}

interface AdGroup {
  dn: string;
  cn: string;
  memberCount: number;
}

interface AdDomain {
  name: string;
  domain_dns: string;
  ldap_url?: string;
  reachable?: boolean;
}

type CreateUserPayload =
  | { mode: 'form'; firstName: string; lastName: string; username: string; email: string; description: string }
  | { mode: 'template'; templateName: string; firstName: string; lastName: string; username: string };

// ── Helpers ──────────────────────────────────────────────────────────────────

function getOu(user: AdUser): string {
  const dn = user.distinguishedName || '';
  const m = dn.match(/OU=([^,]+)/);
  return m ? m[1] : '—';
}

/**
 * Nástroje vrací selhání jako hodnotu `{ ok: false, error }`, ne jako HTTP
 * chybu. Pro react-query se z ní musí stát vyjímka, jinak `isError` zůstane
 * false a UI místo „nedaří se spojit s DC“ vykreslí prázdnou doménu.
 */
type ToolResult<T> = T & { ok?: boolean; error?: string };

function unwrap<T>(result: ToolResult<T>): T {
  if (result?.ok === false) {
    throw new Error(result.error || 'Operace selhala bez chybového textu');
  }
  return result;
}

// ── User Detail Modal ──────────────────────────────────────────────────────

function UserDetailModal({
  user,
  open,
  onClose,
  onPasswordReset,
  onAddGroup,
  groups,
}: {
  user: AdUser | null;
  open: boolean;
  onClose: () => void;
  onPasswordReset: (username: string) => void;
  onAddGroup: (username: string, group: string) => void;
  groups: AdGroup[];
}) {
  const { t } = useTranslation();
  const [newGroup, setNewGroup] = useState('');

  if (!user) return null;

  const displayName = user.displayName || '—';
  const username = user.sAMAccountName || '';
  const email = user.mail || '—';
  const ou = getOu(user);
  const memberOf = Array.isArray(user.memberOf) ? user.memberOf : [];

  return (
    <Modal open={open} onClose={onClose} title={t('adManager.userTitle', { name: displayName })}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <Label>{t('adManager.nameLabel')}</Label>
            <p className="font-medium">{displayName}</p>
          </div>
          <div>
            <Label>Login (sAMAccountName)</Label>
            <p className="font-mono">{username || '—'}</p>
          </div>
          <div>
            <Label>Email</Label>
            <p>{email}</p>
          </div>
          <div>
            <Label>{t('adManager.ouGroup')}</Label>
            <Badge variant="default">{ou}</Badge>
          </div>
          <div className="col-span-2">
            <Label>DN</Label>
            <p className="font-mono text-xs break-all">{user.distinguishedName}</p>
          </div>
          {user.description && (
            <div className="col-span-2">
              <Label>{t('adManager.positionDesc')}</Label>
              <p>{user.description}</p>
            </div>
          )}
        </div>

        {memberOf.length > 0 && (
          <div>
            <Label className="mb-2">{t('adManager.groupsLabel')}</Label>
            <div className="flex flex-wrap gap-1">
              {memberOf.map((g, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {g.split(',').find((x) => x.startsWith('CN='))?.replace('CN=', '') || g}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-4 border-t">
          <Button variant="secondary" onClick={() => onPasswordReset(username)}>
            <KeyRound className="w-4 h-4 mr-1" /> {t('adManager.resetPassword')}
          </Button>
          <div className="flex items-center gap-1 ml-auto">
            <Select value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder={t('adManager.addToGroupPlaceholder')}>
              <option value="">{t('adManager.selectGroup')}</option>
              {groups.map((g) => (
                <option key={g.cn} value={g.cn}>
                  {g.cn} ({g.memberCount})
                </option>
              ))}
            </Select>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (newGroup) {
                  onAddGroup(username, newGroup);
                  setNewGroup('');
                }
              }}
            >
              <UserCheck className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Reset Password Modal ───────────────────────────────────────────────────

function ResetPasswordModal({
  open,
  onClose,
  onReset,
  customPassword,
  setCustomPassword,
  targetUsername,
}: {
  open: boolean;
  onClose: () => void;
  onReset: () => void;
  customPassword: string;
  setCustomPassword: (v: string) => void;
  targetUsername: string;
}) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} title={t('adManager.resetPassword')}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('adManager.resetFor')} <span className="font-mono font-medium">{targetUsername}</span>.{' '}
          {t('adManager.resetHint')}
        </p>
        <div>
          <Label>{t('adManager.customPasswordOptional')}</Label>
          <Input
            type="password"
            value={customPassword}
            onChange={(e) => setCustomPassword(e.target.value)}
            placeholder={t('adManager.phEmptyGenerate')}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={onReset}>
            <KeyRound className="w-4 h-4 mr-1" /> {t('adManager.resetPasswordAction')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Create User Modal ──────────────────────────────────────────────────────

function CreateUserModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (data: CreateUserPayload) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'form' | 'template'>('form');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [templateName, setTemplateName] = useState('');

  const reset = () => {
    setFirstName('');
    setLastName('');
    setUsername('');
    setEmail('');
    setDescription('');
    setTemplateName('');
  };

  const handleSubmit = () => {
    if (!firstName.trim() || !lastName.trim() || !username.trim()) {
      toast.error(t('adManager.namesRequired'));
      return;
    }
    if (mode === 'template' && !templateName) {
      toast.error(t('adManager.selectTemplateError'));
      return;
    }
    if (mode === 'form') {
      onCreate({ mode: 'form', firstName, lastName, username, email, description });
    } else {
      onCreate({ mode: 'template', templateName, firstName, lastName, username });
    }
    reset();
  };

  return (
    <Modal open={open} onClose={onClose} title={t('adManager.createUser')}>
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button variant={mode === 'form' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('form')}>
            {t('adManager.formMode')}
          </Button>
          <Button variant={mode === 'template' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('template')}>
            {t('adManager.fromTemplate')}
          </Button>
        </div>

        {mode === 'template' && (
          <div>
            <Label>{t('adManager.templateRequired')}</Label>
            <Select value={templateName} onChange={(e) => setTemplateName(e.target.value)}>
              <option value="">{t('adManager.selectTemplate')}</option>
              <option value="konstruktér">{t('adManager.tplDesigner', 'Konstruktér')}</option>
              <option value="realizace">{t('adManager.tplRealization', 'Realizace')}</option>
              <option value="výroba">{t('adManager.tplProduction', 'Výroba')}</option>
              <option value="administrativa">{t('adManager.tplAdmin', 'Administrativa')}</option>
              <option value="obchod">{t('adManager.tplSales', 'Obchod')}</option>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t('adManager.firstNameRequired')}</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jan" />
          </div>
          <div>
            <Label>{t('adManager.lastNameRequired')}</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={t('adManager.phLastName', 'Novák')} />
          </div>
        </div>
        <div>
          <Label>Login (sAMAccountName) *</Label>
          <Input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} placeholder="jan.novak" />
        </div>

        {mode === 'form' && (
          <>
            <div>
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jan.novak@domain.cz" />
            </div>
            <div>
              <Label>{t('adManager.positionDesc')}</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('adManager.phPosition', 'Konstruktér')} />
            </div>
          </>
        )}

        <div className="flex gap-2 justify-end pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit}>
            <UserPlus className="w-4 h-4 mr-1" /> {t('adManager.createUser')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── CSV Import Tab ─────────────────────────────────────────────────────────

interface CsvRow {
  firstName: string;
  lastName: string;
  username: string;
  email?: string;
  description?: string;
  ou?: string;
  group?: string;
  template?: string;
}

interface ImportResult {
  row: number;
  username: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
  password?: string;
}

const TEMPLATE_NAMES = ['konstruktér', 'realizace', 'výroba', 'administrativa', 'obchod'];

/**
 * Browser-side CSV parser. Akceptuje `,` nebo `;` jako delimiter,
 * podporuje quoted hodnoty `"..."` s escape `""`.
 * Header row je povinný.
 */
function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim() !== '');
  if (lines.length === 0) return { headers: [], rows: [] };

  const delim = lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',';

  const splitLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (c === '"') {
          inQ = false;
        } else {
          cur += c;
        }
      } else {
        if (c === '"') inQ = true;
        else if (c === delim) {
          out.push(cur);
          cur = '';
        } else {
          cur += c;
        }
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };

  const headers = splitLine(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map((line) => {
    const cells = splitLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h] = cells[i] ?? '';
    });
    return obj;
  });
  return { headers, rows };
}

/** Mapování CSV řádku na CsvRow podle běžných aliasů sloupců. */
function mapRow(raw: Record<string, string>): CsvRow | null {
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      if (raw[k] !== undefined && raw[k].trim() !== '') return raw[k].trim();
    }
    return '';
  };
  const firstName = pick('firstname', 'first_name', 'jméno', 'jmeno', 'name');
  const lastName = pick('lastname', 'last_name', 'příjmení', 'prijmeni', 'surname');
  const username = pick('username', 'login', 'samaccountname', 'uživatel', 'uzivatel');
  if (!firstName || !lastName || !username) return null;
  return {
    firstName,
    lastName,
    username,
    email: pick('email', 'mail', 'e-mail') || undefined,
    description: pick('description', 'popis', 'pozice', 'comment') || undefined,
    ou: pick('ou', 'oddělení', 'oddeleni', 'department') || undefined,
    group: pick('group', 'skupina') || undefined,
    template: pick('template', 'šablona', 'sablona') || undefined,
  };
}

function CsvImportTab({ domain, onDone }: { domain: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [parseError, setParseError] = useState('');
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then((text) => {
      try {
        const { rows: raw } = parseCsv(text);
        const mapped: CsvRow[] = [];
        const errors: number[] = [];
        raw.forEach((r, i) => {
          const m = mapRow(r);
          if (m) mapped.push(m);
          else errors.push(i + 2); // +2 = header + 1-based
        });
        setRows(mapped);
        setResults([]);
        if (errors.length > 0) {
          setParseError(t('adManager.rowsIncomplete', { rows: errors.join(', ') }));
        } else {
          setParseError('');
        }
      } catch (err) {
        setParseError(`Parse error: ${(err as Error).message}`);
        setRows([]);
      }
    });
  };

  const runImport = async () => {
    if (rows.length === 0) return;
    setRunning(true);
    const initial: ImportResult[] = rows.map((r, i) => ({ row: i + 2, username: r.username, status: 'pending' }));
    setResults(initial);

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const template = row.template || defaultTemplate;
      try {
        let data: { userDn: string; newPassword: string };
        if (template) {
          data = await api
            .mcp<ToolResult<{ userDn: string; newPassword: string }>>('ad.template_apply', {
              domain,
              templateName: template,
              firstName: row.firstName,
              lastName: row.lastName,
              username: row.username,
              customFields: {
                email: row.email,
                description: row.description,
                ou: row.ou,
                group: row.group,
              },
            })
            .then(unwrap);
        } else {
          data = await api
            .mcp<ToolResult<{ userDn: string; newPassword: string }>>('ad.create_user', {
              domain,
              firstName: row.firstName,
              lastName: row.lastName,
              username: row.username,
              email: row.email,
              description: row.description,
              ou: row.ou,
              group: row.group,
            })
            .then(unwrap);
        }
        setResults((prev) =>
          prev.map((r, idx) =>
            idx === i
              ? { ...r, status: 'success', message: data.userDn, password: data.newPassword }
              : r,
          ),
        );
      } catch (e) {
        setResults((prev) =>
          prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', message: (e as Error).message } : r,
          ),
        );
      }
    }

    setRunning(false);
    onDone();
  };

  const exportResults = () => {
    if (results.length === 0) return;
    const lines = ['username,status,dn_or_error,password'];
    for (const r of results) {
      const msg = (r.message || '').replace(/"/g, '""');
      const pw = (r.password || '').replace(/"/g, '""');
      lines.push(`${r.username},${r.status},"${msg}","${pw}"`);
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ad-import-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const okCount = results.filter((r) => r.status === 'success').length;
  const errCount = results.filter((r) => r.status === 'error').length;
  const pendCount = results.filter((r) => r.status === 'pending').length;

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="space-y-2">
          <Label>{t('adManager.step1Csv')}</Label>
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleFile}
              className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
            />
            {rows.length > 0 && (
              <Badge variant="default" className="font-mono">
                {t('adManager.rowsReady', { count: rows.length })}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('adManager.csvColsIntro')}{' '}
            <code className="font-mono">firstName, lastName, username</code> {t('adManager.csvRequired')}{' '}
            <code className="font-mono">email, description, ou, group, template</code> {t('adManager.csvOptional')}{' '}
            <code>,</code> {t('adManager.csvOr')} <code>;</code> {t('adManager.csvAutodetect')}
          </p>
          {parseError && (
            <p className="text-sm text-amber-600 dark:text-amber-400">{parseError}</p>
          )}
        </div>

        {rows.length > 0 && (
          <div className="space-y-2">
            <Label>{t('adManager.step2DefaultTemplate')}</Label>
            <Select value={defaultTemplate} onChange={(e) => setDefaultTemplate(e.target.value)}>
              <option value="">{t('adManager.noTemplate')}</option>
              {TEMPLATE_NAMES.map((tn) => (
                <option key={tn} value={tn}>
                  {tn} (ad.template_apply)
                </option>
              ))}
            </Select>
          </div>
        )}

        {rows.length > 0 && results.length === 0 && (
          <div className="border-t pt-4">
            <Label className="mb-2">{t('adManager.step3Preview')}</Label>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-muted/50 text-xs">
                  <tr>
                    <th className="text-left px-2 py-1.5">Login</th>
                    <th className="text-left px-2 py-1.5">{t('adManager.colFirstName')}</th>
                    <th className="text-left px-2 py-1.5">{t('adManager.colLastName')}</th>
                    <th className="text-left px-2 py-1.5">Email</th>
                    <th className="text-left px-2 py-1.5">{t('adManager.colTemplateOu')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1.5 font-mono text-xs">{r.username}</td>
                      <td className="px-2 py-1.5">{r.firstName}</td>
                      <td className="px-2 py-1.5">{r.lastName}</td>
                      <td className="px-2 py-1.5 text-xs">{r.email || '—'}</td>
                      <td className="px-2 py-1.5 text-xs">
                        {r.template || defaultTemplate || (r.ou ? `OU ${r.ou}` : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 10 && (
                <p className="text-xs text-muted-foreground mt-1">{t('adManager.moreRows', { count: rows.length - 10 })}</p>
              )}
            </div>

            <div className="flex gap-2 pt-3">
              <Button onClick={runImport} disabled={running || !domain}>
                <Upload className="w-4 h-4 mr-1" /> {t('adManager.runImport', { count: rows.length })}
              </Button>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm">
                <Badge variant="default">{t('adManager.successCount', { count: okCount })}</Badge>
                {errCount > 0 && <Badge variant="error">{t('adManager.errorCount', { count: errCount })}</Badge>}
                {pendCount > 0 && (
                  <Badge variant="secondary">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />
                    {t('adManager.pendingCount', { count: pendCount })}
                  </Badge>
                )}
              </div>
              {!running && results.length > 0 && (
                <Button variant="secondary" size="sm" onClick={exportResults}>
                  {t('adManager.downloadResults')}
                </Button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-muted/50 text-xs">
                  <tr>
                    <th className="text-left px-2 py-1.5">#</th>
                    <th className="text-left px-2 py-1.5">Login</th>
                    <th className="text-left px-2 py-1.5">{t('adManager.colStatus')}</th>
                    <th className="text-left px-2 py-1.5">{t('adManager.colDnError')}</th>
                    <th className="text-left px-2 py-1.5">{t('adManager.colPassword')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.row} className="border-t">
                      <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.row}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{r.username}</td>
                      <td className="px-2 py-1.5">
                        {r.status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                        {r.status === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
                        {r.status === 'pending' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                      </td>
                      <td className="px-2 py-1.5 text-xs font-mono break-all max-w-md">
                        {r.message || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-xs font-mono">
                        {r.password ? <span className="select-all">{r.password}</span> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function AdManagerPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Domain state — persistent v localStorage
  const [activeDomain, setActiveDomain] = useState<string>(
    () => (typeof window !== 'undefined' && localStorage.getItem(ACTIVE_DOMAIN_KEY)) || ''
  );

  const [search, setSearch] = useState('');
  const [filterOu, setFilterOu] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdUser | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [customPassword, setCustomPassword] = useState('');
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);

  // ── Queries ──────────────────────────────────────────────────────────────

  const domainsQ = useQuery({
    queryKey: ['ad-domains'],
    queryFn: () =>
      api.mcp<ToolResult<{ domains: AdDomain[] }>>('ad.list_domains', {}).then(unwrap),
  });

  const domains = domainsQ.data?.domains ?? [];

  // Auto-select first domain on first load
  useEffect(() => {
    if (!activeDomain && domains.length > 0) {
      setActiveDomain(domains[0].name);
      localStorage.setItem(ACTIVE_DOMAIN_KEY, domains[0].name);
    }
  }, [activeDomain, domains]);

  const usersQ = useQuery({
    queryKey: ['ad-users', activeDomain],
    queryFn: () =>
      api.mcp<ToolResult<{ users: AdUser[]; truncated?: boolean }>>('ad.list_users', {
        domain: activeDomain,
        // UI filtruje lokálně; limit drží odpověď v rozumné velikosti u
        // velkých domén a `truncated` řekne, že seznam není celý.
        limit: 1000,
      }).then(unwrap),
    enabled: !!activeDomain,
  });

  const groupsQ = useQuery({
    queryKey: ['ad-groups', activeDomain],
    queryFn: () =>
      api.mcp<ToolResult<{ groups: AdGroup[] }>>('ad.list_groups', {
        domain: activeDomain,
        limit: 1000,
      }).then(unwrap),
    enabled: !!activeDomain,
  });

  // ── Mutations ────────────────────────────────────────────────────────────

  const resetMut = useMutation({
    mutationFn: (vars: { username: string; newPassword?: string }) =>
      api
        .mcp<ToolResult<{ newPassword: string }>>('ad.reset_password', {
          domain: activeDomain,
          username: vars.username,
          newPassword: vars.newPassword || undefined,
        })
        .then(unwrap),
    onSuccess: (data, variables) => {
      toast.success(t('adManager.passwordResetFor', { username: variables.username }));
      setRevealedPassword(data.newPassword);
      setResetModalOpen(false);
      setCustomPassword('');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createMut = useMutation({
    mutationFn: (payload: CreateUserPayload) => {
      if (payload.mode === 'form') {
        return api
          .mcp<ToolResult<{ userDn: string; newPassword: string }>>('ad.create_user', {
            domain: activeDomain,
            firstName: payload.firstName,
            lastName: payload.lastName,
            username: payload.username,
            email: payload.email || undefined,
            description: payload.description || undefined,
          })
          .then(unwrap);
      }
      return api
        .mcp<ToolResult<{ userDn: string; newPassword: string }>>('ad.template_apply', {
          domain: activeDomain,
          templateName: payload.templateName,
          firstName: payload.firstName,
          lastName: payload.lastName,
          username: payload.username,
        })
        .then(unwrap);
    },
    onSuccess: (data) => {
      toast.success(t('adManager.userCreated', { dn: data.userDn.split(',')[0] }));
      setRevealedPassword(data.newPassword);
      setCreateModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['ad-users'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const addToGroupMut = useMutation({
    mutationFn: (vars: { username: string; groupName: string }) =>
      api
        .mcp<ToolResult<Record<string, never>>>('ad.add_to_group', {
          domain: activeDomain,
          username: vars.username,
          groupName: vars.groupName,
        })
        .then(unwrap),
    onSuccess: (_, vars) => {
      toast.success(t('adManager.addedToGroup', { username: vars.username, group: vars.groupName }));
      queryClient.invalidateQueries({ queryKey: ['ad-users'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Derived ──────────────────────────────────────────────────────────────

  const users = usersQ.data?.users ?? [];
  const groups = groupsQ.data?.groups ?? [];

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchSearch =
        !q ||
        u.displayName?.toLowerCase().includes(q) ||
        u.sAMAccountName?.toLowerCase().includes(q) ||
        u.mail?.toLowerCase().includes(q);
      const matchOu = !filterOu || getOu(u) === filterOu;
      return matchSearch && matchOu;
    });
  }, [users, search, filterOu]);

  const ouOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) {
      const ou = getOu(u);
      if (ou !== '—') set.add(ou);
    }
    return Array.from(set).sort();
  }, [users]);

  const handleDomainChange = (name: string) => {
    setActiveDomain(name);
    localStorage.setItem(ACTIVE_DOMAIN_KEY, name);
  };

  const handleResetPasswordClick = (username: string) => {
    setResetUsername(username);
    setCustomPassword('');
    setResetModalOpen(true);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <PageHeader title="AD Manager" description={t('adManager.pageDescription')}>
        <div className="flex items-center gap-3">
          {domains.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <Select value={activeDomain} onChange={(e) => handleDomainChange(e.target.value)}>
                {domains.map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name} ({d.domain_dns})
                  </option>
                ))}
              </Select>
            </div>
          )}
          <Button onClick={() => setCreateModalOpen(true)} disabled={!activeDomain}>
            <UserPlus className="w-4 h-4 mr-1" /> {t('adManager.newUser')}
          </Button>
        </div>
      </PageHeader>

      {!activeDomain && domainsQ.isFetched && (
        <EmptyState
          icon={Building2}
          title={t('adManager.noDomain')}
          description={t('adManager.noDomainDesc')}
        />
      )}

      {activeDomain && (
        <>
          <StatsGrid>
            <StatCard icon={Users} value={users.length || '—'} label={t('adManager.statUsers')} />
            <StatCard icon={GroupsIcon} value={groups.length || '—'} label={t('adManager.statGroups')} />
            <StatCard icon={Shield} value={domains.length} label={t('adManager.statDomains')} />
            <StatCard icon={Building2} value={ouOptions.length || '—'} label="OU" />
          </StatsGrid>

          <Tabs defaultTab='users'>
            <TabList>
              <Tab id="users">{t('adManager.usersTab')}</Tab>
              <Tab id="groups">{t('adManager.groupsTab')}</Tab>
              <Tab id="import">{t('adManager.importTab')}</Tab>
              <Tab id="audit">Audit log</Tab>
            </TabList>

            <TabPanel id="users">
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={t('adManager.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    {ouOptions.length > 0 && (
                      <Select value={filterOu} onChange={(e) => setFilterOu(e.target.value)} placeholder={t('adManager.filterByOu')}>
                        <option value="">{t('adManager.allOu')}</option>
                        {ouOptions.map((ou) => (
                          <option key={ou} value={ou}>
                            {ou}
                          </option>
                        ))}
                      </Select>
                    )}
                  </div>

                  {usersQ.isLoading ? (
                    <p className="text-sm text-muted-foreground">{t('adManager.loadingLdap')}</p>
                  ) : usersQ.isError ? (
                    <p className="text-sm text-red-600 font-mono">{(usersQ.error as Error).message}</p>
                  ) : filteredUsers.length === 0 ? (
                    <EmptyState
                      icon={Users}
                      title={t('adManager.noUsers')}
                      description={users.length === 0 ? t('adManager.emptyDomain') : t('adManager.noFilterResults')}
                    />
                  ) : (
                    <div className="space-y-1">
                      {filteredUsers.map((user) => (
                        <div
                          key={user.distinguishedName}
                          className="flex items-center justify-between p-2 rounded hover:bg-muted/50 cursor-pointer"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDetail(true);
                          }}
                        >
                          <div>
                            <p className="font-medium">{user.displayName}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {user.sAMAccountName} · {getOu(user)}
                              {user.mail && ` · ${user.mail}`}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel id="groups">
              <Card>
                <CardContent className="p-4">
                  {groupsQ.isLoading ? (
                    <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
                  ) : groupsQ.isError ? (
                    <p className="text-sm text-red-600 font-mono">{(groupsQ.error as Error).message}</p>
                  ) : groups.length === 0 ? (
                    <EmptyState icon={GroupsIcon} title={t('adManager.noGroups')} />
                  ) : (
                    <div className="space-y-1">
                      {groups.map((g) => (
                        <div
                          key={g.dn}
                          className="flex items-center justify-between p-2 rounded hover:bg-muted/50"
                        >
                          <div>
                            <p className="font-medium">{g.cn}</p>
                            <p className="text-xs text-muted-foreground font-mono">{g.dn}</p>
                          </div>
                          <Badge variant="secondary">{t('adManager.membersCount', { count: g.memberCount })}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel id="import">
              <CsvImportTab domain={activeDomain} onDone={() => queryClient.invalidateQueries({ queryKey: ['ad-users', activeDomain] })} />
            </TabPanel>

            <TabPanel id="audit">
              <Card>
                <CardContent className="p-8 text-center text-sm text-muted-foreground">
                  <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  {t('adManager.auditIntro')} <code className="font-mono">acm.ad_manager_audit</code>.
                  <br />
                  {t('adManager.auditToolPrefix')} <code className="font-mono">ad.list_audit</code> {t('adManager.auditToolSuffix')}
                </CardContent>
              </Card>
            </TabPanel>
          </Tabs>
        </>
      )}

      <UserDetailModal
        user={selectedUser}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        onPasswordReset={(u) => {
          setShowDetail(false);
          handleResetPasswordClick(u);
        }}
        onAddGroup={(u, g) => addToGroupMut.mutate({ username: u, groupName: g })}
        groups={groups}
      />

      <ResetPasswordModal
        open={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        onReset={() => resetMut.mutate({ username: resetUsername, newPassword: customPassword })}
        customPassword={customPassword}
        setCustomPassword={setCustomPassword}
        targetUsername={resetUsername}
      />

      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={(data) => createMut.mutate(data)}
      />

      {revealedPassword && (
        <Modal open={true} onClose={() => setRevealedPassword(null)} title={t('adManager.generatedPassword')}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('adManager.passwordShownOnce')}
            </p>
            <div className="p-3 bg-muted rounded font-mono text-lg select-all">{revealedPassword}</div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  navigator.clipboard.writeText(revealedPassword);
                  toast.success(t('adManager.copied'));
                }}
              >
                {t('adManager.copyToClipboard')}
              </Button>
              <Button onClick={() => setRevealedPassword(null)}>{t('adManager.close')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
